import { LocalStorageIo } from '@aninix/api'
import { useGetProjectMeta } from '@aninix/api/get-project-meta'
import { HotkeyCombination, Tooltip } from '@aninix/app-design-system'
import { featureFlags, Tool, useTools } from '@aninix/core'
import { useAddSvgFileToSelectedLayer } from '@aninix/core/use-cases/add-svg-file-to-selected-layer'
import { useGetComments } from '@aninix/core/use-cases/comments/use-get-comments'
import classNames from 'classnames'
import Markdown from 'markdown-to-jsx'
import { observer } from 'mobx-react'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import { ShapeAutoIcon } from './shape-icon'
import { ShapeMenu } from './shape-menu'

const localProjectCommentsCount = new LocalStorageIo<Record<string, number>>(
  'aninix.project-comments-counters',
  {}
)

export const Tools: React.FCC = observer(() => {
  const { projectId } = useParams()
  const { data, refetch } = useGetProjectMeta(projectId ?? '')
  const [shapeMenuOpen, setShapeMenuOpen] = React.useState(false)
  const shapeButtonRef = React.useRef<HTMLButtonElement>(null)

  const { query: getCommentsQuery } = useGetComments({
    isStartingLoading: false,
  })

  React.useEffect(() => {
    // this will ensure metadata of the project is reloaded
    refetch()
  }, [getCommentsQuery?.data, projectId])

  const [displayCommentsBadge, setDisplayCommentsBadge] =
    React.useState<boolean>(false)

  const tools = useTools()

  const addFile = useAddSvgFileToSelectedLayer()

  const [lastTool, setLastTool] = React.useState(tools.activeTool)

  const handleMediaInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.currentTarget?.files?.[0]

    console.log(file)

    if (file != null) {
      addFile(file)
    }
  }

  React.useEffect(() => {
    const projectCommentsCount = data?.data.totalCommentsCount ?? 0

    if (projectId === undefined) return

    // this will remove badge on changing tool to comments and update localStorage
    if (tools.activeTool === Tool.Comments && projectCommentsCount > 0) {
      localProjectCommentsCount.update({ [projectId]: projectCommentsCount })
      setDisplayCommentsBadge(false)
    }
  }, [tools.activeTool, data?.data.totalCommentsCount, projectId])

  React.useEffect(() => {
    const projectCommentsCount = data?.data.totalCommentsCount ?? 0

    if (projectId === undefined) return

    const value = localProjectCommentsCount.get()
    // this will check if badge is required on initial state (when localStorage is empty)
    if (value === null || value[projectId] === undefined) {
      localProjectCommentsCount.update({ [projectId]: 0 })
      if (projectCommentsCount > 0) setDisplayCommentsBadge(true)
      return
    }

    // this will check if badge is required if all the data is here
    if (projectCommentsCount > value[projectId]) {
      setDisplayCommentsBadge(true)
      return
    }

    // since early returns, if no checks has been triggered, then badge is not required
    setDisplayCommentsBadge(false)
  }, [data?.data.totalCommentsCount, localProjectCommentsCount, projectId])

  return (
    <div className="flex flex-row gap-2 items-center pl-2 relative">
      <ToolButton
        tool={Tool.Selection}
        Icon={SelectionIcon}
        tooltip="Move _V_"
      />
      {featureFlags.shapesCreation && (
        <ToolButton tool={Tool.Frame} Icon={FrameIcon} tooltip="Frame" />
      )}
      {featureFlags.shapesCreation && (
        <ToolButton
          tool={Tool.Shape}
          Icon={ShapeAutoIcon}
          onClick={() => setShapeMenuOpen(true)}
          ref={shapeButtonRef}
          tooltip="Shape"
        />
      )}
      <ToolButton tool={Tool.Hand} Icon={HandIcon} tooltip="Hand _H_" />
      <ToolButton
        tool={Tool.Comments}
        Icon={CommentsIcon}
        displayBadge={displayCommentsBadge}
        tooltip="Comments _C_"
      />
      {featureFlags.importSvg && (
        <label>
          <input
            className="hidden"
            onClick={() => {}}
            onChangeCapture={handleMediaInput}
            type="file"
            accept=".svg"
          />
          <ToolLikeDiv Icon={InsertMediaIcon} tooltip="Import SVG" />
        </label>
      )}
      <ShapeMenu
        open={shapeMenuOpen}
        anchorEl={shapeButtonRef.current}
        onClose={() => setShapeMenuOpen(false)}
      />
    </div>
  )
})

interface IToolButton {
  tool: Tool
  Icon: (props: React.HTMLAttributes<SVGSVGElement>) => React.JSX.Element
  displayBadge?: boolean
  onClick?: () => void
  removeClick?: boolean
  tooltip?: string
}

const toolOrder = [
  Tool.Selection,
  Tool.Frame,
  Tool.Shape,
  Tool.Hand,
  Tool.Comments,
]

const ToolButton = React.forwardRef<HTMLButtonElement, IToolButton>(
  (
    { tool, Icon, displayBadge, onClick, removeClick = false, tooltip = '' },
    ref
  ) => {
    const tools = useTools()

    const isActive = tools.activeTool === tool

    const offset = toolOrder.indexOf(tools.activeTool) - toolOrder.indexOf(tool)

    const toRight = offset > 0

    const toLeft = offset < 0

    const isFar = Math.abs(offset) >= 2

    return (
      <Tooltip
        title={
          tooltip === '' ? (
            ''
          ) : (
            <Markdown
              options={{
                overrides: {
                  em: (props) => <HotkeyCombination keys={[props.children]} />,
                },
              }}
            >
              {tooltip}
            </Markdown>
          )
        }
      >
        <button
          onClick={
            removeClick
              ? undefined
              : () => {
                  onClick?.()
                  tools.changeTool(tool)
                }
          }
          className={classNames(
            'group relative flex h-8 w-8 items-center justify-center rounded-lg hover:cursor-default'
          )}
          ref={ref}
        >
          {/* Badge */}
          <div
            className={classNames(
              'absolute right-0.5 top-0.5 z-20 h-2 w-2 origin-center rounded-full bg-red transition-opacity duration-150',
              { ['scale-0 opacity-0']: !displayBadge }
            )}
          />

          <div
            className={classNames(
              'absolute inset-0 z-0 rounded-lg bg-white opacity-0 transition-opacity duration-150',
              { ['group-hover:!opacity-10']: !isActive }
            )}
          />
          <div
            className={classNames(
              'pointer-events-none absolute inset-0 z-0 rounded-lg bg-accent transition-[transform,opacity] duration-150 ease-in-out',
              {
                ['opacity-0']: !isActive,
                ['translate-x-10']: toRight && !isFar,
                ['-translate-x-10']: toLeft && !isFar,
                ['translate-x-20']: toRight && isFar,
                ['-translate-x-20']: toLeft && isFar,
              }
            )}
          />
          <Icon
            className={classNames('z-10 h-6 w-6', {
              ['opacity-50']: !isActive,
            })}
          />
        </button>
      </Tooltip>
    )
  }
)

const ToolLikeDiv = React.forwardRef<
  HTMLDivElement,
  Pick<IToolButton, 'displayBadge' | 'Icon' | 'tooltip'>
>(({ Icon, displayBadge, tooltip = '' }, ref) => {
  return (
    <Tooltip
      title={
        tooltip === '' ? (
          ''
        ) : (
          <Markdown
            options={{
              overrides: {
                em: (props) => <HotkeyCombination keys={[props.children]} />,
              },
            }}
          >
            {tooltip}
          </Markdown>
        )
      }
    >
      <div
        className={classNames(
          'group relative flex h-8 w-8 items-center justify-center rounded-lg hover:cursor-default'
        )}
        ref={ref}
      >
        {/* Badge */}
        <div
          className={classNames(
            'absolute right-0.5 top-0.5 z-20 h-2 w-2 origin-center rounded-full bg-red transition-opacity duration-150',
            { ['scale-0 opacity-0']: !displayBadge }
          )}
        />
        <div
          className={classNames(
            'absolute inset-0 z-0 rounded-lg bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-150'
          )}
        />
        <div
          className={classNames(
            'absolute inset-0 z-0 rounded-lg opacity-0 bg-accent group-active:opacity-100 transition-opacity duration-150'
          )}
        />
        <Icon
          className={classNames(
            'z-10 h-6 w-6 opacity-50 group-active:opacity-100'
          )}
        />
      </div>
    </Tooltip>
  )
})

const SelectionIcon = (props: React.HTMLAttributes<SVGSVGElement>) => (
  <svg
    {...props}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.9046 17.5651C7.95779 17.804 8.08278 18.021 8.2628 18.1869C8.44282 18.3528 8.66927 18.4597 8.91176 18.4932C9.15426 18.5267 9.40121 18.4853 9.61948 18.3745C9.83776 18.2636 10.0169 18.0887 10.1329 17.8731L12.2233 14.7797L17.131 19.6874C17.2301 19.7865 17.3478 19.8651 17.4772 19.9188C17.6067 19.9724 17.7455 20 17.8856 20C18.0258 20 18.1646 19.9724 18.294 19.9188C18.4235 19.8651 18.5412 19.7865 18.6403 19.6874L19.6874 18.6403C19.7865 18.5412 19.8651 18.4235 19.9188 18.294C19.9724 18.1646 20 18.0258 20 17.8856C20 17.7455 19.9724 17.6067 19.9188 17.4772C19.8651 17.3478 19.7865 17.2301 19.6874 17.131L14.7797 12.2233L17.8931 10.1329C18.1087 10.0168 18.2836 9.8376 18.3944 9.61926C18.5051 9.40091 18.5465 9.15391 18.5128 8.9114C18.4792 8.66888 18.3722 8.44246 18.2062 8.2625C18.0402 8.08254 17.8231 7.95765 17.5841 7.9046L4 4L7.9046 17.5651Z"
      stroke="white"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const FrameIcon = (props: React.HTMLAttributes<SVGSVGElement>) => (
  <svg
    {...props}
    width="24"
    height="24"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 10.5H13M3 5.5H13M5.5 3V13M10.5 3L10.5 13"
      stroke="white"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const HandIcon = (props: React.HTMLAttributes<SVGSVGElement>) => (
  <svg
    {...props}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.7308 6.15C10.7308 5.79196 10.5886 5.44858 10.3354 5.19541C10.0822 4.94223 9.73887 4.8 9.38083 4.8C9.02279 4.8 8.67941 4.94223 8.42624 5.19541C8.17306 5.44858 8.03083 5.79196 8.03083 6.15V12.9L6.70783 11.577C6.44282 11.3123 6.09695 11.1437 5.72517 11.0981C5.35339 11.0524 4.97702 11.1323 4.65583 11.325C4.35428 11.5058 4.13516 11.7971 4.04507 12.1369C3.95498 12.4768 4.00103 12.8384 4.17343 13.1448C5.86453 16.1508 6.85003 17.8689 7.13083 18.3C7.1894 18.3902 7.2482 18.4802 7.30723 18.57C7.79909 19.3169 8.46875 19.9299 9.25607 20.3541C10.0434 20.7782 10.9237 21.0002 11.818 21H11.6308H13.4308C14.863 21 16.2365 20.4311 17.2492 19.4184C18.2619 18.4057 18.8308 17.0322 18.8308 15.6V7.95C18.8308 7.59196 18.6886 7.24858 18.4354 6.99541C18.1822 6.74223 17.8389 6.6 17.4808 6.6C17.1228 6.6 16.7794 6.74223 16.5262 6.99541C16.2731 7.24858 16.1308 7.59196 16.1308 7.95M10.7308 6.15V12M10.7308 6.15V4.35C10.7308 4.17272 10.7657 3.99717 10.8336 3.83338C10.9014 3.66959 11.0009 3.52076 11.1262 3.39541C11.2516 3.27005 11.4004 3.17061 11.5642 3.10276C11.728 3.03492 11.9035 3 12.0808 3C12.2581 3 12.4337 3.03492 12.5975 3.10276C12.7612 3.17061 12.9101 3.27005 13.0354 3.39541C13.1608 3.52076 13.2602 3.66959 13.3281 3.83338C13.3959 3.99717 13.4308 4.17272 13.4308 4.35V12M13.4308 6.15C13.4308 5.79196 13.5731 5.44858 13.8262 5.19541C14.0794 4.94223 14.4228 4.8 14.7808 4.8C15.1389 4.8 15.4822 4.94223 15.7354 5.19541C15.9886 5.44858 16.1308 5.79196 16.1308 6.15V12"
      stroke="white"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CommentsIcon = (props: React.HTMLAttributes<SVGSVGElement>) => (
  <svg
    {...props}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 20.0001L4.3 16.1001C3.17644 14.4384 2.76999 12.4705 3.15622 10.5624C3.54244 8.65427 4.69506 6.93575 6.39977 5.72635C8.10447 4.51696 10.2453 3.89898 12.4241 3.98732C14.6029 4.07566 16.6715 4.86431 18.2453 6.20664C19.819 7.54896 20.7909 9.35362 20.9801 11.2851C21.1693 13.2165 20.563 15.1433 19.2739 16.7072C17.9848 18.2711 16.1007 19.3657 13.9718 19.7874C11.8429 20.2092 9.6142 19.9294 7.7 19.0001L3 20.0001Z"
      stroke="white"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const InsertMediaIcon = (props: React.HTMLAttributes<SVGSVGElement>) => (
  <svg
    {...props}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 13.6H3.2H4ZM4 10.4H4.8H4ZM10.4 4V3.2V4ZM13.6 4V4.8V4ZM20 10.4H19.2H20ZM19.2 12C19.2 12.4418 19.5582 12.8 20 12.8C20.4418 12.8 20.8 12.4418 20.8 12H19.2ZM12 20.8C12.4418 20.8 12.8 20.4418 12.8 20C12.8 19.5582 12.4418 19.2 12 19.2V20.8ZM10.4 20V20.8V20ZM4.43597 6.18404L5.14878 6.54723V6.54723L4.43597 6.18404ZM6.18404 4.43597L5.82085 3.72317L6.18404 4.43597ZM4.43597 17.816L5.14878 17.4528L4.43597 17.816ZM6.18404 19.564L6.54723 18.8512H6.54723L6.18404 19.564ZM17.816 4.43597L17.4528 5.14878V5.14878L17.816 4.43597ZM19.564 6.18404L20.2768 5.82085L19.564 6.18404ZM3.46851 14.4021C3.13828 14.6956 3.10854 15.2013 3.40207 15.5315C3.69561 15.8617 4.20127 15.8915 4.53149 15.5979L3.46851 14.4021ZM7.83564 11.5905L8.36713 12.1885L7.83564 11.5905ZM9.16436 11.5905L9.69586 10.9926H9.69586L9.16436 11.5905ZM12.4685 15.5979C12.7987 15.8915 13.3044 15.8617 13.5979 15.5315C13.8915 15.2013 13.8617 14.6956 13.5315 14.4021L12.4685 15.5979ZM13.5 11.625L14.0406 11.0353C13.7347 10.7549 13.2653 10.7549 12.9594 11.0353L13.5 11.625ZM14.4594 13.5897C14.7851 13.8883 15.2912 13.8663 15.5897 13.5406C15.8883 13.2149 15.8663 12.7088 15.5406 12.4103L14.4594 13.5897ZM11.4594 12.4103C11.1337 12.7088 11.1117 13.2149 11.4103 13.5406C11.7088 13.8663 12.2149 13.8883 12.5406 13.5897L11.4594 12.4103ZM18.8 15C18.8 14.5582 18.4418 14.2 18 14.2C17.5582 14.2 17.2 14.5582 17.2 15H18.8ZM17.2 21C17.2 21.4418 17.5582 21.8 18 21.8C18.4418 21.8 18.8 21.4418 18.8 21H17.2ZM21 18.8C21.4418 18.8 21.8 18.4418 21.8 18C21.8 17.5582 21.4418 17.2 21 17.2V18.8ZM15 17.2C14.5582 17.2 14.2 17.5582 14.2 18C14.2 18.4418 14.5582 18.8 15 18.8V17.2ZM4.8 13.6L4.8 10.4H3.2L3.2 13.6H4.8ZM10.4 4.8L13.6 4.8V3.2L10.4 3.2V4.8ZM19.2 10.4V12H20.8V10.4H19.2ZM12 19.2H10.4V20.8H12V19.2ZM4.8 10.4C4.8 9.26669 4.80062 8.46483 4.85184 7.83795C4.90229 7.22042 4.99783 6.84348 5.14878 6.54723L3.72317 5.82085C3.43814 6.38024 3.31569 6.99118 3.25715 7.70766C3.19938 8.4148 3.2 9.2931 3.2 10.4H4.8ZM10.4 3.2C9.2931 3.2 8.4148 3.19938 7.70766 3.25715C6.99118 3.31569 6.38024 3.43814 5.82085 3.72317L6.54723 5.14878C6.84348 4.99783 7.22042 4.90229 7.83795 4.85184C8.46483 4.80062 9.26669 4.8 10.4 4.8V3.2ZM5.14878 6.54723C5.45557 5.94511 5.94511 5.45557 6.54723 5.14878L5.82085 3.72317C4.91767 4.18336 4.18336 4.91767 3.72317 5.82085L5.14878 6.54723ZM3.2 13.6C3.2 14.7069 3.19938 15.5852 3.25715 16.2923C3.31569 17.0088 3.43814 17.6198 3.72317 18.1792L5.14878 17.4528C4.99783 17.1565 4.90229 16.7796 4.85184 16.162C4.80062 15.5352 4.8 14.7333 4.8 13.6H3.2ZM10.4 19.2C9.26669 19.2 8.46483 19.1994 7.83795 19.1482C7.22042 19.0977 6.84348 19.0022 6.54723 18.8512L5.82085 20.2768C6.38024 20.5619 6.99118 20.6843 7.70766 20.7428C8.4148 20.8006 9.2931 20.8 10.4 20.8V19.2ZM3.72317 18.1792C4.18336 19.0823 4.91767 19.8166 5.82085 20.2768L6.54723 18.8512C5.94511 18.5444 5.45557 18.0549 5.14878 17.4528L3.72317 18.1792ZM13.6 4.8C14.7333 4.8 15.5352 4.80062 16.162 4.85184C16.7796 4.90229 17.1565 4.99783 17.4528 5.14878L18.1792 3.72317C17.6198 3.43814 17.0088 3.31569 16.2923 3.25715C15.5852 3.19938 14.7069 3.2 13.6 3.2V4.8ZM20.8 10.4C20.8 9.2931 20.8006 8.4148 20.7428 7.70766C20.6843 6.99118 20.5619 6.38024 20.2768 5.82085L18.8512 6.54723C19.0022 6.84348 19.0977 7.22042 19.1482 7.83795C19.1994 8.46483 19.2 9.26669 19.2 10.4H20.8ZM17.4528 5.14878C18.0549 5.45557 18.5444 5.94511 18.8512 6.54723L20.2768 5.82085C19.8166 4.91767 19.0823 4.18336 18.1792 3.72317L17.4528 5.14878ZM4.53149 15.5979L8.36713 12.1885L7.30415 10.9926L3.46851 14.4021L4.53149 15.5979ZM8.63287 12.1885L12.4685 15.5979L13.5315 14.4021L9.69586 10.9926L8.63287 12.1885ZM8.36713 12.1885C8.4429 12.1211 8.5571 12.1211 8.63287 12.1885L9.69586 10.9926C9.01386 10.3864 7.98614 10.3864 7.30415 10.9926L8.36713 12.1885ZM12.9594 12.2147L14.4594 13.5897L15.5406 12.4103L14.0406 11.0353L12.9594 12.2147ZM12.5406 13.5897L14.0406 12.2147L12.9594 11.0353L11.4594 12.4103L12.5406 13.5897ZM17.2 15V21H18.8V15H17.2ZM21 17.2H15V18.8H21V17.2ZM16.2 8C16.2 8.11046 16.1105 8.2 16 8.2V9.8C16.9941 9.8 17.8 8.99411 17.8 8H16.2ZM16 8.2C15.8895 8.2 15.8 8.11046 15.8 8H14.2C14.2 8.99411 15.0059 9.8 16 9.8V8.2ZM15.8 8C15.8 7.88954 15.8895 7.8 16 7.8V6.2C15.0059 6.2 14.2 7.00589 14.2 8H15.8ZM16 7.8C16.1105 7.8 16.2 7.88954 16.2 8H17.8C17.8 7.00589 16.9941 6.2 16 6.2V7.8Z"
      fill="white"
    />
  </svg>
)

Tools.displayName = 'Tools'
